import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useGames } from '../../helpers/useApi';
import { GameWithDate, Pagination } from '../../types/types';
import Layout from './Layout';
import {
  CardActionArea,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { formatLongDisplayDate, isGameOver } from '../../helpers';
import { gameNumber } from '../../helpers/dates';
import { loadGameFromLocalStorage } from '../../helpers/localStorage';

const defaultPagination: Pagination = {
  total: 0,
  page: 1,
  limit: 10,
  pages: 1,
  maxLimit: 20,
};

function ArchivePage() {
  const [page, setPage] = useState(1);
  const limit = defaultPagination.limit;
  const navigate = useNavigate();

  const { data, isLoading, isError } = useGames(page, limit);

  if (isLoading)
    return (
      <Layout>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          mt={8}
          mb={2}
        >
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            Loading Archive…
          </Typography>
          <CircularProgress size={40} sx={{ color: 'black' }} />
        </Box>
      </Layout>
    );

  const { data: games, pagination } = data ?? {
    data: [],
    pagination: defaultPagination,
  };

  return (
    <Layout>
      <Box width="100%" display="flex" flexDirection="column" gap={4}>
        <Typography variant="h6" component="h1" align="center" marginTop={6}>
          Welcome to the Wormle Archive.
        </Typography>
        <Typography maxWidth={600}>
          Here you can play every game I've ever released. Archive games won't
          affect your streak or statistics.
        </Typography>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Box
            width="fit-content"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap={2}
          >
            {games.map((game, index) => (
              <GameCard
                key={game.date}
                game={game}
                onClick={() => navigate(`/${index === 0 ? '' : game.date}`)}
                isLatest={index === 0 && page === 1}
              />
            ))}
          </Box>
        </Box>

        <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            onClick={() => setPage((p) => p - 1)}
            disabled={page <= 1}
          >
            Prev
          </Button>

          <Typography variant="body2">
            Page {pagination.page} of {pagination.pages}
          </Typography>

          <Button
            variant="outlined"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= pagination.pages}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Layout>
  );
}

const isGameComplete = (dateStr: string) => {
  const local = loadGameFromLocalStorage(dateStr);
  if (!local) return false;
  const isComplete = isGameOver(local.gameState);
  return isComplete;
};

function getCardColor(gameNumber: number) {
  const remainder = gameNumber % 3;
  return `var(--tile-color-${remainder + 1})`;
}

function GameCard(props: {
  game: GameWithDate;
  onClick: () => void;
  isLatest?: boolean;
}) {
  const { game, onClick, isLatest } = props;
  const isComplete = isGameComplete(game.date);

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        borderRadius: 2,
        backgroundColor: isComplete
          ? getCardColor(gameNumber(game.date))
          : 'var(--empty-tile-color)',
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
        <CardContent sx={{ pb: 1, pt: 1 }}>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            gap={4}
          >
            <Box display="flex" gap={4} alignItems="center">
              <Typography variant="h6">#{gameNumber(game.date)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {isLatest ? 'Today' : formatLongDisplayDate(game.date)}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {isComplete ? (
                <CheckCircleOutlineIcon color="action" fontSize="small" />
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontStyle="italic"
                >
                  Not Complete
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Box>
  );
}

export default ArchivePage;
